package org.example.zone360.pages.coachDetails.coachInfo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.example.zone360.models.GetSingleCoachData
import org.example.zone360.theme.toSitePalette
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.HTMLCanvasElement

@Composable
fun CoachInfoDonut(
    coachData: GetSingleCoachData,
    isSavingPic: Boolean = false
) {
    val sitePalette = ColorMode.current.toSitePalette()
    val canvasId = if (isSavingPic) Res.Id.infoDonutChartSnapShot else Res.Id.infoDonutChart

    // labels & data
    val xValues = listOf("Technical", "Fitness", "Tactical", "Mental", "Goalkeeper", "Custom")
    val donutData = remember(coachData.donut) { coachData.donut }
    val yValues = listOf(
        donutData.technical ?: 0,
        donutData.fitness ?: 0,
        donutData.tactical ?: 0,
        donutData.mental ?: 0,
        donutData.goalkeeper ?: 0,
        donutData.custom ?: 0
    )

    // colors
    val barColors = listOf(
        sitePalette.technicalCategoryBorder.hex,
        sitePalette.fitnessCategoryBorder.hex,
        sitePalette.tacticalCategoryBorder.hex,
        sitePalette.mentalCategoryBorder.hex,
        sitePalette.goalKeeperCategoryBorder.hex,
        "#FB6F2A" // Custom
    )

    // use a square canvas to keep the donut perfectly circular
    val chartSizePx = if (isSavingPic) 80 else 150

    Canvas(
        Modifier
            .width(chartSizePx.px)
            .height(chartSizePx.px)
            .id(canvasId)
            .toAttrs()
    ) {}

    LaunchedEffect(yValues) {
        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LaunchedEffect

        // draw at a square resolution (important for a round donut)
        canvas.width = chartSizePx
        canvas.height = chartSizePx

        val adjustedYValues = if (yValues.all { it == 0 }) yValues.map { 1 } else yValues

        val script = """
            (function() {
                const existing = Chart.getChart("$canvasId");
                if (existing) existing.destroy();

                const xValues = ${xValues.joinToString(prefix = "[", postfix = "]") { "\"$it\"" }};
                const yValues = [${adjustedYValues.joinToString(",")}];
                const barColors = ${barColors.joinToString(prefix = "[", postfix = "]") { "\"$it\"" }};
                const total = yValues.reduce((a, b) => a + b, 0);

                new Chart("$canvasId", {
                    type: "doughnut",
                    data: {
                        labels: xValues,
                        datasets: [{
                            backgroundColor: barColors,
                            data: yValues
                        }]
                    },
                    options: {
                        plugins: {
                            legend: { display: false },
                            title: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: function(ctx) {
                                        const value = yValues[ctx.dataIndex];
                                        const pct = ((value / (total || 1)) * 100).toFixed(2);
                                        return ctx.label + ": " + pct + "%";
                                    }
                                }
                            }
                        },
                        cutout: '75%',
                        responsive: false,
                        maintainAspectRatio: false   // <- important with fixed square canvas
                    }
                });
            })();
        """.trimIndent()

        val scriptElement = document.createElement("script")
        scriptElement.textContent = script
        document.body?.appendChild(scriptElement)
    }
}


